import { useEffect, useState } from "react";
import { Plus, Trash2, Search, Shield, GraduationCap, Users, Link2, KeyRound, Pencil, ClipboardCheck, ToggleLeft, ToggleRight, ShieldCheck, BriefcaseBusiness } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { norm } from "@/lib/format";
import { usePermisos } from "@/lib/permisos";
import { MatrizPermisos } from "@/components/MatrizPermisos";
import { SelectorVista, useVistaEstudiantes } from "@/components/VistasEstudiantes";
import { VistaUsuarios } from "@/components/VistasUsuarios";
import { Loading, PageTitle } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const ROLES = [
  { v: "superadmin", label: "SuperAdmin", icon: Shield },
  { v: "admin", label: "Administrador", icon: Shield },
  { v: "coordinadora", label: "Coordinadora", icon: ClipboardCheck },
  { v: "secretaria", label: "Secretaria", icon: BriefcaseBusiness },
  { v: "docente", label: "Docente", icon: GraduationCap },
  { v: "tutor", label: "Padre / Tutor", icon: Users },
];

const nuevo = { nombre: "", email: "", password: "", rol: "tutor", telefono: "", direccion: "", cedula_rnc: "" };

export default function AdminUsuarios() {
  const [rows, setRows] = useState(null);
  const [estudiantes, setEstudiantes] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [filtroRol, setFiltroRol] = useState("todos");
  const [form, setForm] = useState(null);
  const [vincular, setVincular] = useState(null);
  const [seleccion, setSeleccion] = useState([]);
  const [clave, setClave] = useState(null);
  const [editar, setEditar] = useState(null);
  const [demo, setDemo] = useState(null);
  const [pestana, setPestana] = useState("usuarios");
  const { vista, agrupado, cambiarVista, cambiarAgrupado } = useVistaEstudiantes("usuarios");
  const { puede } = usePermisos();
  const todasLasCuentas = puede("usuarios:gestionar");
  const rolesDisponibles = todasLasCuentas ? ROLES : ROLES.filter((r) => r.v === "tutor");

  const cargar = () => {
    api.get("/usuarios").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/estudiantes").then((r) => setEstudiantes(r.data));
    api.get("/usuarios/demo").then((r) => setDemo(r.data)).catch(() => setDemo(null));
  };

  useEffect(cargar, []);

  const alternarDemo = async (habilitado) => {
    try {
      await api.post("/usuarios/demo", { habilitado });
      toast.success(habilitado
        ? "Cuentas de demostración habilitadas"
        : "Cuentas de demostración deshabilitadas: ya no pueden iniciar sesión");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const alternarUsuario = async (u) => {
    try {
      await api.put(`/usuarios/${u.id}/activo`, { activo: u.activo === false });
      toast.success(u.activo === false ? `${u.nombre} puede iniciar sesión` : `${u.nombre} quedó desactivado`);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const crear = async () => {
    if (!form.nombre.trim()) return toast.error("Indique el nombre");
    if (!form.email.trim()) return toast.error("Indique el correo");
    if (form.password.length < 6) return toast.error("La contraseña debe tener al menos 6 caracteres");
    try {
      await api.post("/usuarios", form);
      toast.success(`Usuario creado con rol ${form.rol}`);
      setForm(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const cambiarRol = async (u, rol) => {
    try {
      await api.put(`/usuarios/${u.id}`, { rol });
      toast.success(`${u.nombre} ahora tiene el rol ${rol}`);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const eliminar = async (u) => {
    try {
      await api.delete(`/usuarios/${u.id}`);
      toast.success("Usuario eliminado");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const abrirVinculo = (u) => {
    setVincular(u);
    setSeleccion(estudiantes.filter((e) => e.tutor_id === u.id).map((e) => e.id));
  };

  const guardarVinculo = async (reasignar = false) => {
    try {
      await api.put(`/usuarios/${vincular.id}/estudiantes`, { estudiante_ids: seleccion, reasignar });
      toast.success("Estudiantes vinculados al tutor");
      setVincular(null);
      cargar();
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error(apiError(e), {
          action: { label: "Reasignar", onClick: () => guardarVinculo(true) },
          duration: 8000,
        });
        return;
      }
      toast.error(apiError(e));
    }
  };

  const guardarDatos = async () => {
    if (!editar.nombre.trim()) return toast.error("Indique el nombre");
    if (!editar.email.trim() || !editar.email.includes("@")) return toast.error("Indique un correo válido");
    try {
      await api.put(`/usuarios/${editar.id}`, {
        nombre: editar.nombre.trim(),
        email: editar.email.trim(),
        telefono: editar.telefono || null,
        direccion: editar.direccion || null,
        cedula_rnc: editar.cedula_rnc || null,
      });
      toast.success("Datos del usuario actualizados");
      setEditar(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const guardarClave = async () => {
    try {
      await api.put(`/usuarios/${clave.u.id}`, { password: clave.valor });
      toast.success("Contraseña actualizada");
      setClave(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!rows) return <Loading />;

  const q = norm(busqueda.trim());
  const visibles = rows.filter(
    (u) =>
      (filtroRol === "todos" || u.rol === filtroRol) &&
      (!q || [u.nombre, u.email, u.telefono].filter(Boolean).some((v) => norm(v).includes(q)))
  );

  const accionesUsuario = (u) => (
    <>
      <Button size="sm" variant="outline" className="rounded-full" data-testid={`editar-usuario-${u.email}`}
              onClick={() => setEditar({ ...u, telefono: u.telefono || "", direccion: u.direccion || "",
                                         cedula_rnc: u.cedula_rnc || "" })}>
        <Pencil className="mr-1 h-3 w-3" /> Editar datos
      </Button>
      <Button size="sm" variant="outline" className="rounded-full"
              data-testid={`alternar-usuario-${u.email}`} onClick={() => alternarUsuario(u)}>
        {u.activo === false
          ? <><ToggleLeft className="mr-1 h-3 w-3" /> Activar</>
          : <><ToggleRight className="mr-1 h-3 w-3" /> Desactivar</>}
      </Button>
      {u.rol === "tutor" && (
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => abrirVinculo(u)}
                data-testid={`vincular-${u.email}`}>
          <Link2 className="mr-1 h-3 w-3" /> Vincular hijos
        </Button>
      )}
      <Button size="sm" variant="outline" className="rounded-full"
              onClick={() => setClave({ u, valor: "" })} data-testid={`clave-${u.email}`}>
        <KeyRound className="mr-1 h-3 w-3" /> Clave
      </Button>
      {todasLasCuentas && (
        <Button size="sm" variant="ghost" onClick={() => eliminar(u)} data-testid={`eliminar-usuario-${u.email}`}>
          <Trash2 className="h-3 w-3 text-destructive" />
        </Button>
      )}
    </>
  );

  const tarjetaUsuario = (u) => (
    <article key={u.id} className="lift rounded-lg border border-border/60 bg-card p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-display text-base font-semibold">{u.nombre}</h3>
          <p className="truncate font-mono text-xs text-muted-foreground">{u.email}</p>
        </div>
        <Badge variant={["admin", "superadmin"].includes(u.rol) ? "default"
          : u.rol === "docente" ? "secondary" : "outline"}>
          {u.rol}
        </Badge>
      </div>
      {u.activo === false && (
        <Badge variant="destructive" className="mt-3" data-testid={`inactivo-${u.email}`}>
          Cuenta desactivada
        </Badge>
      )}
      {u.sin_acceso && (
        <Badge variant="outline" className="mt-3" data-testid={`sin-acceso-${u.id}`}>
          Ficha de contacto · sin acceso al portal
        </Badge>
      )}

      {u.rol === "tutor" && (
        <div className="mt-4 space-y-1 text-sm">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Cédula / RNC</p>
          <p className={u.cedula_rnc ? "font-mono text-xs" : "text-xs text-destructive"}
             data-testid={`cedula-${u.email}`}>
            {u.cedula_rnc || "Sin registrar (requerida para la factura fiscal)"}
          </p>
          <p className="pt-2 text-xs uppercase tracking-widest text-muted-foreground">Estudiantes</p>
          {u.estudiantes?.length
            ? u.estudiantes.map((e) => <p key={e.id} className="text-muted-foreground">• {e.nombre}</p>)
            : <p className="text-muted-foreground">Sin estudiantes vinculados</p>}
        </div>
      )}
      {u.rol === "docente" && (
        <div className="mt-4 space-y-1 text-sm">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Materias</p>
          {u.materias?.length
            ? u.materias.map((m) => <p key={m.id} className="text-muted-foreground">• {m.nombre} ({m.grado})</p>)
            : <p className="text-muted-foreground">Sin materias asignadas</p>}
        </div>
      )}

      <div className="mt-5 space-y-3">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Rol</Label>
          <select value={u.rol} onChange={(e) => cambiarRol(u, e.target.value)}
                  data-testid={`rol-${u.email}`} disabled={!todasLasCuentas}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm disabled:opacity-60">
            {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-2">{accionesUsuario(u)}</div>
      </div>
    </article>
  );

  return (
    <div data-testid="admin-usuarios">
      <PageTitle title="Usuarios, roles y permisos"
                 subtitle="Administración centralizada de cuentas, aislamiento de portales por rol y edición en vivo de los privilegios del sistema.">
        {pestana === "usuarios" && (
          <Button className="rounded-full" onClick={() => setForm({ ...nuevo })} data-testid="nuevo-usuario">
            <Plus className="mr-2 h-4 w-4" /> Nuevo usuario
          </Button>
        )}
      </PageTitle>

      <div className="mb-8 flex flex-wrap gap-2 border-b border-border pb-3">
        <button onClick={() => setPestana("usuarios")} data-testid="pestana-usuarios"
                className={`rounded-full px-5 py-2 text-sm lift ${
                  pestana === "usuarios" ? "bg-primary text-primary-foreground font-semibold"
                                         : "text-muted-foreground hover:bg-muted"}`}>
          <Users className="mr-2 inline h-4 w-4" /> Usuarios
        </button>
        {puede("permisos:gestionar") && (
          <button onClick={() => setPestana("permisos")} data-testid="pestana-permisos"
                  className={`rounded-full px-5 py-2 text-sm lift ${
                    pestana === "permisos" ? "bg-primary text-primary-foreground font-semibold"
                                           : "text-muted-foreground hover:bg-muted"}`}>
            <ShieldCheck className="mr-2 inline h-4 w-4" /> Matriz de permisos
          </button>
        )}
      </div>

      {pestana === "permisos" ? <MatrizPermisos /> : (
      <>
      {demo && (
        <div className="mb-8 rounded-lg border border-border bg-muted/30 p-5" data-testid="panel-cuentas-demo">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-display text-base font-semibold">Cuentas de demostración</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                {demo.cuentas.length} cuenta(s) de prueba (@demo.com). Al deshabilitarlas no podrán iniciar sesión
                y se ocultan los accesos rápidos de la pantalla de login.
              </p>
            </div>
            <Button className="rounded-full" variant={demo.habilitado ? "outline" : "default"}
                    onClick={() => alternarDemo(!demo.habilitado)} data-testid="alternar-demo">
              {demo.habilitado ? (
                <><ToggleRight className="mr-2 h-4 w-4" /> Deshabilitar cuentas demo</>
              ) : (
                <><ToggleLeft className="mr-2 h-4 w-4" /> Habilitar cuentas demo</>
              )}
            </Button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {demo.cuentas.map((c) => (
              <Badge key={c.id} variant={c.activo ? "secondary" : "outline"}
                     data-testid={`demo-estado-${c.email}`}>
                {c.email} · {c.activo ? "activa" : "desactivada"}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="rounded-full pl-10" placeholder="Buscar por nombre, correo o teléfono…"
                 value={busqueda} onChange={(e) => setBusqueda(e.target.value)} data-testid="buscar-usuario" />
        </div>
        {["todos", ...ROLES.map((r) => r.v)].map((r) => (
          <button key={r} onClick={() => setFiltroRol(r)} data-testid={`filtro-rol-${r}`}
                  className={`rounded-full border px-4 py-2 text-sm capitalize lift ${
                    filtroRol === r ? "border-primary bg-primary/5 font-semibold" : "border-border"}`}>
            {r}
          </button>
        ))}
        <SelectorVista vista={vista} agrupado={agrupado} onVista={cambiarVista}
                       onAgrupado={cambiarAgrupado} etiquetaAgrupar="Agrupar por rol"
                       prefijo="vista-usuarios" tidAgrupar="agrupar-rol" />
      </div>

      <VistaUsuarios usuarios={visibles} vista={vista} agrupado={agrupado}
                     acciones={accionesUsuario} tarjeta={tarjetaUsuario} />
      </>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="modal-usuario">
          <DialogHeader>
            <DialogTitle className="font-display">Nuevo usuario</DialogTitle>
            <DialogDescription>La cuenta podrá iniciar sesión de inmediato con el rol asignado.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Nombre completo</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                       data-testid="usuario-nombre" />
              </div>
              <div className="space-y-2">
                <Label>Correo</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                       data-testid="usuario-email" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Contraseña</Label>
                  <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                         data-testid="usuario-password" />
                </div>
                <div className="space-y-2">
                  <Label>Rol</Label>
                  <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                          value={form.rol} onChange={(e) => setForm({ ...form, rol: e.target.value })}
                          data-testid="usuario-rol">
                    {rolesDisponibles.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                         data-testid="usuario-telefono" />
                </div>
                <div className="space-y-2">
                  <Label>Cédula o RNC {form.rol === "tutor" ? "(para la factura)" : ""}</Label>
                  <Input value={form.cedula_rnc || ""} placeholder="001-1234567-8" className="font-mono"
                         data-testid="usuario-cedula"
                         onChange={(e) => setForm({ ...form, cedula_rnc: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Dirección</Label>
                <Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} />
              </div>
              <Button className="rounded-full py-6" onClick={crear} data-testid="guardar-usuario">Crear usuario</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!vincular} onOpenChange={(o) => !o && setVincular(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="modal-vinculo">
          <DialogHeader>
            <DialogTitle className="font-display">Vincular estudiantes</DialogTitle>
            <DialogDescription>Seleccione los hijos de {vincular?.nombre}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {estudiantes.map((e) => (
              <label key={e.id} className="flex items-center gap-3 rounded-md border border-border/60 p-3 text-sm">
                <Checkbox checked={seleccion.includes(e.id)} data-testid={`vinculo-${e.matricula}`}
                          onCheckedChange={(v) =>
                            setSeleccion(v ? [...seleccion, e.id] : seleccion.filter((x) => x !== e.id))} />
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{e.nombre}</span>
                  <span className="ml-2 font-mono text-xs text-muted-foreground">{e.matricula}</span>
                </span>
                {e.tutor_nombre && e.tutor_id !== vincular?.id && (
                  <Badge variant="outline" className="text-[10px]">tutor: {e.tutor_nombre}</Badge>
                )}
              </label>
            ))}
            <p className="rounded-md bg-muted px-4 py-3 text-xs leading-relaxed text-muted-foreground">
              Marque solo los estudiantes que corresponden a este tutor. Si un estudiante ya está vinculado a otro
              tutor, el sistema le pedirá confirmar la reasignación.
            </p>
            <Button className="w-full rounded-full py-6" onClick={() => guardarVinculo(false)}
                    data-testid="guardar-vinculo">
              Guardar vínculos
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editar} onOpenChange={(o) => !o && setEditar(null)}>
        <DialogContent data-testid="modal-editar-usuario">
          <DialogHeader>
            <DialogTitle className="font-display">Editar datos del usuario</DialogTitle>
            <DialogDescription>
              El correo es el usuario de acceso: al cambiarlo, la persona inicia sesión con el nuevo correo.
            </DialogDescription>
          </DialogHeader>
          {editar && (
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Nombre completo</Label>
                <Input value={editar.nombre} data-testid="editar-nombre"
                       onChange={(e) => setEditar({ ...editar, nombre: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Correo</Label>
                <Input type="email" value={editar.email} data-testid="editar-email"
                       onChange={(e) => setEditar({ ...editar, email: e.target.value })} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input value={editar.telefono} data-testid="editar-telefono"
                         onChange={(e) => setEditar({ ...editar, telefono: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Cédula o RNC</Label>
                  <Input value={editar.cedula_rnc || ""} placeholder="001-1234567-8" className="font-mono"
                         data-testid="editar-cedula"
                         onChange={(e) => setEditar({ ...editar, cedula_rnc: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Dirección</Label>
                <Input value={editar.direccion}
                       onChange={(e) => setEditar({ ...editar, direccion: e.target.value })} />
              </div>
              {editar.rol === "tutor" && !editar.cedula_rnc && (
                <p className="rounded-md bg-muted px-4 py-3 text-xs text-muted-foreground">
                  La cédula o RNC de la familia se imprime en la factura fiscal: sin ella solo se
                  puede emitir como consumidor final.
                </p>
              )}
              <Button className="rounded-full py-6" onClick={guardarDatos} data-testid="guardar-datos-usuario">
                Guardar cambios
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!clave} onOpenChange={(o) => !o && setClave(null)}>
        <DialogContent data-testid="modal-clave">
          <DialogHeader>
            <DialogTitle className="font-display">Restablecer contraseña</DialogTitle>
            <DialogDescription>{clave?.u?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {clave?.u?.email?.endsWith("@registro.local") && (
              <p className="rounded-md bg-amber-500/10 px-4 py-3 text-sm text-amber-700"
                 data-testid="aviso-correo-ficha">
                Este tutor todavía tiene un correo interno del sistema, así que la contraseña no le
                servirá para entrar. Use primero "Editar datos" y coloque su correo real.
              </p>
            )}
            <div className="space-y-2">
              <Label>Nueva contraseña</Label>
              <Input value={clave?.valor || ""} data-testid="clave-nueva"
                     onChange={(e) => setClave({ ...clave, valor: e.target.value })} />
            </div>
            <Button className="w-full rounded-full py-6" onClick={guardarClave} data-testid="guardar-clave">
              Actualizar contraseña
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
